<template>
	<div
		class=" bg-white rounded-b-md px-4 pt-4 max-h-full min-h-0 overflow-hidden flex flex-col space-y-2"
		:class="!listType && 'h-full border-t border-border-secondary md:border-none md:bg-transparent md:rounded-none md:p-0'"
	>
		<slot name="header" />
		<div v-if="showSearch" :class="{'md:hidden': !listType}">
			<FormKit
				preserve
				type="form"
				:actions="false"
				role="search"
				aria-label="Vyhledávání pobočky"
				@submit.prevent
			>
				<FormKit
					v-model="searchFormModel"
					type="search"
					label="Hledat pobočku"
					placeholder="Například Brno"
					:maxlength="100"
					:classes="{
						outer: 'flex flex-auto',
						wrapper: 'flex flex-auto',
						inner: 'bg-background-secondary xl:border-border-primary w-full !max-w-none !px-3 xl:rounded-lg',
						input: '!py-[0.5rem] !px-2.5 flex-auto !text-base xl:!py-[.725rem]',
						label: 'sr-only'
					}"
				>
					<template #prefixIcon>
						<NuxtIcon name="search" filled class="text-icon-primary text-[1.375rem] xl:text-[1.1rem] shrink-0" />
					</template>
				</FormKit>
			</FormKit>
		</div>
		<div v-if="displayedResults.length === 0" class="grow shrink overflow-auto custom-scrollbar border-t border-t-border-secondary mt-2 pt-4 flex flex-col items-center justify-center text-center px-4">
			<NuxtIcon name="frown" class="text-icon-primary rem:text-[48px]" filled />
			<p class="typo-body-s text-body-secondary mt-4">
				Nenašli jsme žádné možnosti, které by odpovídaly vašemu vyhledávání.
			</p>
		</div>
		<div
			v-else
			class="flex shrink max-h-full min-h-0 overflow-auto flex-1"
			:class="!listType && 'md:grow w-full lg:w-auto'"
		>
			<div
				class="flex flex-col w-full max-h-full min-h-0"
				:class="!listType && 'md:block'"
				role="radiogroup"
				:aria-label="listType ? 'Seznam poboček' : undefined"
			>
				<slot name="legend" />
				<slot name="warning" />
				<ul
					class="shrink max-h-full min-h-0 overscroll-contain overflow-auto grid grid-cols-1 items-stretch w-full pb-4"
					:class="[
						!listType && 'md:clear-both md:grid-flow-col md:auto-cols-fr md:grid-rows-6 lg:grid-rows-4 md:w-auto md:bg-transparent',
						wideVariant && 'lg:grid-rows-4',
					]"
				>
					<li
						v-for="option in displayedOptions"
						:key="option.value"
						:class="option.sectionHeader ? 'col-span-full' : ''"
					>
						<div
							v-if="option.sectionHeader"
							class="typo-body-s font-normal text-neutral-800 flex items-center gap-2 w-full px-3.5 py-4"
						>
							<NuxtIcon
								:name="option.icon"
								filled
								aria-hidden="true"
							/>
							{{ option.label }}
							<Tooltip v-if="option.info" class="ml-auto" bubble-class="!max-w-[15rem] text-xs" right-arrow>
								<template #default>
									<span class="text-xs text-body-highlight underline underline-offset-1 flex items-center gap-1">
										{{ option.info.title }}
										<NuxtIcon name="info" filled class="text-body-highlight cursor-help" />
									</span>
								</template>
								<template #tooltip>
									{{ option.info.tooltip }}
								</template>
							</Tooltip>
						</div>
						<div
							v-else
							class="group/option grid focus-within:bg-background-noticeable rounded-full underline-offset-1"
							:class="isSelected(option) && 'checked'"
							@mouseenter="mouseEnterHandler(option.value)"
							@mouseleave="mouseLeaveHandler"
						>
							<button
								type="button"
								class="!m-0 px-3.5 py-1 rem:min-h-[48px] rounded-full gap-3 flex items-center w-full text-left"
								:class="option.disabled ? 'cursor-not-allowed text-body-quaternary' : 'cursor-pointer text-body-primary'"
								role="radio"
								:aria-checked="isSelected(option)"
								:disabled="option.disabled"
								@click="clickHandler(option)"
								@keydown="keydownHandler($event, option)"
							>
								<NuxtIcon
									name="chevron-right"
									class="text-icon-primary"
									filled
								/>
								<span class="inline-flex flex-wrap items-baseline font-bold leading-tight underline-offset-2 gap-y-0.5 gap-x-2">
									<span class="text-base" :class="!option.disabled && 'group-hover/option:underline'">
										{{ option.label }}
									</span>
									<span v-if="option.disabledText" class="text-xs font-normal">
										{{ option.disabledText }}
									</span>
									<div
										v-if="option.value === selectedHypermarket"
										class="text-brand-globus-green-dark bg-functional-success-secondary font-normal px-2 py-1 rounded-full typo-body-caption no-underline"
									>
										Vybraná
									</div>
								</span>
							</button>
						</div>
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {localeIncludes} from 'locale-includes';
	import {useGtm} from '@gtm-support/vue-gtm';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';

	type Option = {
		id?: string | number;
		label: string;
		value: string;
		disabled?: boolean;
		disabledText?: string;
		sectionHeader?: boolean;
		icon?: string;
		info?: {
			title: string;
			tooltip: string;
		};
	};

	type OptionSection = {
		label: string;
		value: string;
		icon: string;
		info?: Option['info'];
		options: Option[];
	};

	type Props = {
		selected: string | null | undefined;
		highlighted?: string | null;
		listType?: boolean;
		wideVariant?: boolean;
		options?: Option[];
		showSearch?: boolean;
	};

	type Emits = {
		(e: 'update:selected', payload: string): void;
		(e: 'update:highlighted', payload: string | null): void;
		(e: 'update:clicked', payload: string): void;
	};

	const props = withDefaults(defineProps<Props>(), {
		highlighted: null,
		options: () => [],
		showSearch: true,
	});
	const emit = defineEmits<Emits>();

	const gtm = useGtm();

	const {selected, highlighted} = useVModels(props, emit);
	const hypermarketsStore = useStoryblokHypermarketsStore();

	const searchFormModel = ref('');

	const {branches, selectedHypermarket} = storeToRefs(hypermarketsStore);

	const storeOptions = computed<Option[]>(() => branches.value?.map((hm) => ({
		id: hm.id,
		label: hm.content.name,
		value: hm.slug,
	})) ?? []);

	const displayedResults = computed(() => {
		let results = props.options.length ? props.options : storeOptions.value;

		if (searchFormModel.value) {
			results = results.filter((item) => localeIncludes(
				item.label,
				searchFormModel.value,
				{sensitivity: 'base'},
			));
		}

		return results;
	});

	const displayedOptions = computed<Option[]>(() => {
		const sections: OptionSection[] = [
			{
				label: 'Hypermarkety',
				value: '__section-hypermarkets',
				icon: 'custom/company',
				options: displayedResults.value.filter((item) => !isFreshBranchOption(item)),
			},
			{
				label: 'Prodejny Fresh',
				value: '__section-fresh-stores',
				icon: 'custom/house',
				info: {
					title: 'Co je fresh?',
					tooltip: 'Fresh je malý formát našich prodejen, kde naleznete specifické zboží pro danou lokalitu.',
				},
				options: displayedResults.value.filter(isFreshBranchOption),
			},
		];

		return sections.flatMap((section) => section.options.length
			? [createSectionHeader(section), ...section.options]
			: []);
	});

	const previousHypermarket = ref(selectedHypermarket.value);

	const commitSelectedDebounced = useDebounceFn(() => {
		changeHypermarket();
	}, 500);

	function changeHypermarket() {
		gtm?.push({
			event: 'branch_change',
			old: previousHypermarket.value,
			new: selectedHypermarket.value,
		});

		previousHypermarket.value = selectedHypermarket.value;
	}

	function isSelected(option: Option) {
		return selected.value === option.value;
	}

	function mouseEnterHandler(optionValue: string) {
		if (isSectionHeaderValue(optionValue)) {
			return;
		}

		highlighted.value = optionValue;
	}

	function mouseLeaveHandler() {
		highlighted.value = null;
	}

	function keydownHandler(event: KeyboardEvent, option: Option) {
		if (option.disabled) {
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			selected.value = option.value;
			commitSelectedDebounced();
		}
	}

	function clickHandler(option: Option) {
		if (option.disabled) {
			return;
		}

		selected.value = option.value;
		emit('update:clicked', option.value);
		commitSelectedDebounced();
	}

	function isFreshBranchOption(option: Option) {
		return option.value.startsWith('fresh-');
	}

	function isSectionHeaderValue(value: string) {
		return value.startsWith('__section-');
	}

	function createSectionHeader(section: Omit<OptionSection, 'options'>): Option {
		return {
			label: section.label,
			value: section.value,
			disabled: true,
			sectionHeader: true,
			icon: section.icon,
			info: section.info,
		};
	}
</script>
